import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Check, Crown, Gift, Coins, Eye, Zap, Users, Shield, ArrowRight } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';

const SubscriptionTierWidget = ({ trialStatus, userRole, usage, creditBalance = 0, pilotStatus }) => {
  const navigate = useNavigate();

  if (!trialStatus) return null;
  
  // Don't show subscription widget for active PILOT users
  if (pilotStatus === 'active') return null;

  const { 
    is_trial, 
    trial_ended, 
    days_remaining, 
    subscription_tier = 'free',
    plan_config
  } = trialStatus;

  const isPM = userRole === 'project_manager';
  const itemName = isPM ? 'project' : 'resource';
  const itemNamePlural = isPM ? 'projects' : 'resources';

  // Get limits from plan_config or defaults
  const maxCount = plan_config?.max_active_items || 1;
  const canInitiateMatching = plan_config?.can_initiate_matching || false;
  const creditsAllowed = plan_config?.credits_allowed || false;
  const creditsPerActivation = plan_config?.credits_per_activation || 100;

  const usageCount = usage?.current_activated || usage?.activated_count || 0;
  const usagePercent = maxCount === 999 ? 0 : Math.min((usageCount / maxCount) * 100, 100);

  // Determine status
  const getStatus = () => {
    if (trial_ended) return 'expired';
    if (is_trial) return 'trial';
    if (subscription_tier === 'free') return 'free';
    return 'active';
  };

  const status = getStatus();

  // Status configurations
  const statusConfig = {
    free: {
      bg: 'bg-slate-50',
      border: 'border-slate-200',
      text: 'text-slate-700',
      badge: 'bg-slate-100 text-slate-600',
      icon: Eye,
      iconBg: 'bg-slate-100',
      iconColor: 'text-slate-500',
      label: 'Passive'
    },
    trial: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-700',
      badge: 'bg-blue-100 text-blue-700',
      icon: Gift,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      label: 'Trial'
    },
    active: {
      bg: subscription_tier === 'professional' ? 'bg-purple-50' : 
          subscription_tier === 'advanced' ? 'bg-emerald-50' : 'bg-blue-50',
      border: subscription_tier === 'professional' ? 'border-purple-200' : 
              subscription_tier === 'advanced' ? 'border-emerald-200' : 'border-blue-200',
      text: subscription_tier === 'professional' ? 'text-purple-700' : 
            subscription_tier === 'advanced' ? 'text-emerald-700' : 'text-blue-700',
      badge: subscription_tier === 'professional' ? 'bg-purple-100 text-purple-700' : 
             subscription_tier === 'advanced' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700',
      icon: subscription_tier === 'professional' ? Shield : 
            subscription_tier === 'advanced' ? Users : Zap,
      iconBg: subscription_tier === 'professional' ? 'bg-purple-100' : 
              subscription_tier === 'advanced' ? 'bg-emerald-100' : 'bg-blue-100',
      iconColor: subscription_tier === 'professional' ? 'text-purple-600' : 
                 subscription_tier === 'advanced' ? 'text-emerald-600' : 'text-blue-600',
      label: 'Active'
    },
    expired: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-700',
      badge: 'bg-red-100 text-red-700',
      icon: AlertTriangle,
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      label: 'Expired'
    }
  };

  const config = statusConfig[status];
  const StatusIcon = config.icon;

  // Plan display names
  const planDisplayName = {
    free: 'Free',
    starter: 'Starter',
    advanced: 'Advanced',
    professional: 'Professional'
  }[subscription_tier] || 'Free';

  const planTagline = {
    free: 'Passive Visibility',
    starter: 'Proactive Matching',
    advanced: 'Team Collaboration',
    professional: 'Confidentiality & Control'
  }[subscription_tier] || '';

  return (
    <Card className={`${config.border} border-2 overflow-hidden`} data-testid="subscription-widget">
      <CardHeader className={`pb-3 ${config.bg}`}>
        <CardTitle className="flex items-center justify-between">
          <span className="text-base font-semibold text-gray-900">Plan & Usage</span>
          {subscription_tier === 'professional' && (
            <Crown className="w-5 h-5 text-yellow-500" />
          )}
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-4 pt-4">
        {/* Plan Info */}
        <div className={`${config.bg} rounded-lg p-4 ${config.border} border`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${config.iconBg}`}>
                <StatusIcon className={`w-5 h-5 ${config.iconColor}`} />
              </div>
              <div>
                <span className={`font-bold ${config.text}`}>{planDisplayName}</span>
                <p className="text-xs text-gray-500">{planTagline}</p>
              </div>
            </div>
            <span className={`text-xs px-2 py-1 rounded-full ${config.badge} font-medium`}>
              {status === 'trial' ? `${days_remaining}d left` : 
               status === 'expired' ? 'Expired' : 
               status === 'free' ? 'Passive' : 'Active'}
            </span>
          </div>

          {/* Status messages */}
          {status === 'trial' && (
            <p className="text-sm text-gray-600 mt-2">
              🎁 Enjoy your free trial!
            </p>
          )}
          {status === 'expired' && (
            <p className="text-sm text-red-600 font-medium mt-2">
              ⚠️ Choose a plan to continue
            </p>
          )}
          {status === 'free' && (
            <p className="text-sm text-gray-600 mt-2">
              👁️ Others can find and contact you
            </p>
          )}
          {status === 'active' && !is_trial && canInitiateMatching && (
            <p className="text-sm text-gray-600 mt-2">
              ✓ AI matching enabled
            </p>
          )}
        </div>

        {/* Usage Stats */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              Active {itemNamePlural}
            </span>
            <span className="text-sm font-bold text-gray-900">
              {usageCount} / {maxCount === 999 ? '∞' : maxCount}
            </span>
          </div>

          {/* Progress bar */}
          {maxCount !== 999 && (
            <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  usagePercent >= 100 ? 'bg-red-500' :
                  usagePercent >= 80 ? 'bg-amber-500' :
                  'bg-emerald-500'
                }`}
                style={{ width: `${Math.min(usagePercent, 100)}%` }}
              />
            </div>
          )}
        </div>

        {/* Credit Balance */}
        {creditBalance > 0 && (
          <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
            <div className="flex items-center gap-2">
              <Coins className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-medium text-amber-800">Available credits</span>
            </div>
            <span className="text-sm font-bold text-amber-800">{creditBalance}</span>
          </div>
        )}

        {/* CTA Buttons */}
        {status === 'expired' && (
          <Button
            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
            onClick={() => navigate('/pricing')}
            data-testid="choose-plan-btn"
          >
            Choose a Plan
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        )}

        {status === 'trial' && days_remaining <= 7 && (
          <Button
            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
            onClick={() => navigate('/pricing')}
            data-testid="view-plans-btn"
          >
            View Plans
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        )}

        {status === 'free' && (
          <Button
            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
            onClick={() => navigate('/pricing')}
            data-testid="upgrade-proactive-btn"
          >
            <Zap className="w-4 h-4 mr-2" />
            Go Proactive
          </Button>
        )}

        {/* Usage warnings */}
        {status !== 'expired' && maxCount !== 999 && (
          <>
            {usageCount > maxCount && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-xs text-red-700 font-semibold">
                  ⚠️ Over limit! Some {itemNamePlural} may be deactivated.
                </p>
                <Button
                  size="sm"
                  className="mt-2 w-full text-xs bg-red-600 hover:bg-red-700"
                  onClick={() => navigate('/pricing')}
                >
                  Upgrade Now
                </Button>
              </div>
            )}
            {usageCount === maxCount && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-amber-700 font-semibold">
                  ⚠️ Limit reached ({maxCount} {itemNamePlural})
                </p>
                {creditsAllowed && creditBalance >= creditsPerActivation ? (
                  <p className="text-xs text-amber-600 mt-1">
                    Use {creditsPerActivation} credits to activate more
                  </p>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2 w-full text-xs border-amber-300 text-amber-700 hover:bg-amber-100"
                    onClick={() => navigate('/pricing')}
                  >
                    {creditsAllowed ? 'Buy Credits' : 'View Upgrade Options'}
                  </Button>
                )}
              </div>
            )}
            {usagePercent >= 80 && usagePercent < 100 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2">
                <p className="text-xs text-amber-700">
                  💡 Approaching limit ({usageCount}/{maxCount})
                </p>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default SubscriptionTierWidget;
